import React, { useState, useEffect, useRef } from 'react';
import { Check, Sparkles, Eye, X, Palette } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import useThemeStore from '../../store/themeStore';
import { toast } from 'sonner';

// Color Picker Component
const ColorPicker = ({ color, onChange, label }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [tempColor, setTempColor] = useState(color);
  const pickerRef = useRef(null);

  useEffect(() => {
    setTempColor(color);
  }, [color]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={pickerRef}>
      <div 
        className="w-12 h-12 rounded-none border-2 border-gray-300 cursor-pointer hover:border-gray-500 transition-colors flex items-center justify-center group"
        style={{ backgroundColor: tempColor }}
        onClick={() => setShowPicker(!showPicker)}
        title={`${label}: ${tempColor} - Klicken zum Ändern`}
      >
        <Palette className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
      </div>
      {showPicker && (
        <div className="absolute z-50 mt-2 p-3 bg-white rounded-none shadow-xl border" style={{ minWidth: '200px' }}>
          <p className="text-xs font-medium mb-2">{label}</p>
          <input
            type="color"
            value={tempColor}
            onChange={(e) => {
              setTempColor(e.target.value);
              onChange(e.target.value);
            }}
            className="w-full h-10 cursor-pointer rounded-none border-0"
          />
          <Input
            type="text"
            value={tempColor}
            onChange={(e) => {
              const val = e.target.value;
              if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                setTempColor(val);
                if (val.length === 7) onChange(val);
              }
            }}
            className="mt-2 text-xs font-mono rounded-none"
            placeholder="#RRGGBB"
          />
        </div>
      )}
    </div>
  );
};

const SeasonalThemesNew = () => {
  const activeTheme = useThemeStore(state => state.activeTheme);
  const setActiveTheme = useThemeStore(state => state.setActiveTheme);
  const [activeSeason, setActiveSeason] = useState('standard');
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTheme, setPreviewTheme] = useState(null);
  const [customColors, setCustomColors] = useState({});

  // Load active seasonal theme on mount
  useEffect(() => {
    loadActiveTheme();
  }, []);

  const loadActiveTheme = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/seasonal/active`);
      if (response.ok) {
        const data = await response.json();
        if (data.seasonalTheme) {
          setActiveSeason(data.seasonalTheme);
        }
      }
    } catch (error) {
      console.error('Error loading active theme:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate decorations for preview and activation
  const generateDecorations = (themeId) => {
    const decorationConfig = {
      christmas: {
        emojis: [
          { emoji: '🎄', sizes: [40, 50, 60, 70], weight: 3 },  // Große Weihnachtsbäume
          { emoji: '❄️', sizes: [15, 20, 25], weight: 5 },      // Viele kleine Schneeflocken
          { emoji: '⭐', sizes: [25, 30, 35], weight: 3 },       // Sterne
          { emoji: '🎅', sizes: [35, 40], weight: 2 },           // Weihnachtsmann
          { emoji: '🎁', sizes: [30, 35, 40], weight: 3 },       // Geschenke
          { emoji: '☃️', sizes: [35, 40], weight: 2 },           // Schneemänner
          { emoji: '🔔', sizes: [25, 30, 35], weight: 3 },       // Glocken
          { emoji: '🕯️', sizes: [20, 25], weight: 2 },           // Kerzen
          { emoji: '🎀', sizes: [25, 30], weight: 2 },           // Schleifen
          { emoji: '🧦', sizes: [25, 30], weight: 1 }            // Socken
        ],
        count: 50
      },
      easter: {
        emojis: [
          { emoji: '🐰', sizes: [35, 40], weight: 3 },
          { emoji: '🌸', sizes: [20, 25, 30], weight: 4 },
          { emoji: '🥚', sizes: [25, 30], weight: 3 },
          { emoji: '🌷', sizes: [30, 35], weight: 3 },
          { emoji: '🦋', sizes: [20, 25], weight: 2 }
        ],
        count: 40
      },
      summer: {
        emojis: [
          { emoji: '☀️', sizes: [40, 50], weight: 2 },
          { emoji: '🌊', sizes: [30, 35], weight: 3 },
          { emoji: '🏖️', sizes: [25, 30], weight: 2 },
          { emoji: '🍉', sizes: [30, 35], weight: 2 },
          { emoji: '🌺', sizes: [25, 30], weight: 3 }
        ],
        count: 35
      },
      autumn: {
        emojis: [
          { emoji: '🍂', sizes: [20, 25, 30], weight: 4 },
          { emoji: '🍁', sizes: [25, 30, 35], weight: 4 },
          { emoji: '🎃', sizes: [35, 40], weight: 2 },
          { emoji: '🌰', sizes: [20, 25], weight: 2 }
        ],
        count: 40
      },
      valentines: {
        emojis: [
          { emoji: '💕', sizes: [25, 30, 35], weight: 4 },
          { emoji: '💝', sizes: [30, 35], weight: 3 },
          { emoji: '🌹', sizes: [30, 35], weight: 3 }
        ],
        count: 35
      },
      mothersday: {
        emojis: [
          { emoji: '💐', sizes: [35, 40], weight: 3 },
          { emoji: '🌸', sizes: [25, 30], weight: 4 },
          { emoji: '🦋', sizes: [20, 25], weight: 2 }
        ],
        count: 35
      }
    };

    const config = decorationConfig[themeId];
    if (!config) return [];

    const decorations = [];
    
    // Create weighted emoji array
    const weightedEmojis = [];
    config.emojis.forEach(item => {
      for (let w = 0; w < item.weight; w++) {
        weightedEmojis.push(item);
      }
    });
    
    // Generate decorations with better distribution
    for (let i = 0; i < config.count; i++) {
      const emojiConfig = weightedEmojis[Math.floor(Math.random() * weightedEmojis.length)];
      const size = emojiConfig.sizes[Math.floor(Math.random() * emojiConfig.sizes.length)];
      
      // Better distribution - avoid clustering
      let x, y;
      const attempts = 10;
      for (let attempt = 0; attempt < attempts; attempt++) {
        x = 5 + Math.random() * 90; // Keep away from edges
        y = i * (2000 / config.count) + (Math.random() * 100 - 50); // Spread vertically along page
        
        // Check if too close to existing decorations
        const tooClose = decorations.some(d => {
          const dist = Math.sqrt(Math.pow(d.position.x - x, 2) + Math.pow(d.position.y - y, 2));
          return dist < 50 && Math.abs(d.size - size) < 20;
        });
        
        if (!tooClose || attempt === attempts - 1) break;
      }
      
      decorations.push({
        instanceId: `decoration_${i}`,
        emoji: emojiConfig.emoji,
        position: { x, y },
        size,
        rotation: Math.random() * 360,
        opacity: 0.4 + Math.random() * 0.4
      });
    }
    
    return decorations;
  };

  // Seasonal Theme Definitions
  const seasonalThemes = [
    {
      id: 'standard',
      name: 'Standard Design',
      description: 'Ihr Hauptdesign ohne saisonale Akzente',
      icon: '⚙️',
      colors: {
        primary: '#C4A084',
        secondary: '#B89968',
        background: '#E8E0D5',
        accent: '#6D5A47'
      },
      decorations: []
    },
    {
      id: 'christmas',
      name: 'Weihnachten',
      description: 'Festliche Weihnachts-Atmosphäre',
      icon: '🎄',
      season: 'Winter',
      period: 'November - Dezember',
      colors: {
        primary: '#C4A084',
        secondary: '#B89968',
        background: '#E8E0D5',
        accent: '#C41E3A', // Weihnachtsrot
        accentSecondary: '#0F5132' // Tannengrün
      },
      decorations: ['❄️', '🎄', '🎅', '⭐', '🎁', '☃️'],
      features: [
        'Schneeflocken-Animation',
        'Weihnachtsbaum-Icons',
        'Rote & Grüne Akzente',
        'Festliche Banner'
      ]
    },
    {
      id: 'easter',
      name: 'Ostern',
      description: 'Frühlingsfrische mit Pastellfarben',
      icon: '🐰',
      season: 'Frühling',
      period: 'März - April',
      colors: {
        primary: '#C4A084',
        secondary: '#B89968',
        background: '#F5F0EB',
        accent: '#FFB6C1', // Rosa
        accentSecondary: '#98D8C8' // Mint
      },
      decorations: ['🐰', '🌸', '🥚', '🌷', '🌼', '🦋'],
      features: [
        'Blumen-Akzente',
        'Pastellfarben',
        'Osterhasen-Icons',
        'Frühlingsfrische'
      ]
    },
    {
      id: 'summer',
      name: 'Sommer',
      description: 'Sommerliche Atmosphäre mit warmen Tönen',
      icon: '☀️',
      season: 'Sommer',
      period: 'Juni - August',
      colors: {
        primary: '#FFB84D',
        secondary: '#FFA500',
        background: '#FFF9E6',
        accent: '#FF6B35',
        accentSecondary: '#4ECDC4'
      },
      decorations: ['☀️', '🌊', '🏖️', '🍉', '🌺', '🦜'],
      features: [
        'Sonnen-Animationen',
        'Warme Sommerfarben',
        'Strand-Icons',
        'Tropische Akzente'
      ]
    },
    {
      id: 'autumn',
      name: 'Herbst',
      description: 'Herbstliche Gemütlichkeit',
      icon: '🍂',
      season: 'Herbst',
      period: 'September - Oktober',
      colors: {
        primary: '#D2691E',
        secondary: '#CD853F',
        background: '#FFF8DC',
        accent: '#B8860B',
        accentSecondary: '#8B4513'
      },
      decorations: ['🍂', '🍁', '🎃', '🌰', '🍄', '🦔'],
      features: [
        'Fallende Blätter',
        'Warme Herbstfarben',
        'Kürbis-Akzente',
        'Gemütliche Atmosphäre'
      ]
    },
    {
      id: 'valentines',
      name: 'Valentinstag',
      description: 'Romantische Atmosphäre für Verliebte',
      icon: '💕',
      season: 'Winter',
      period: 'Februar',
      colors: {
        primary: '#FF69B4',
        secondary: '#FFB6C1',
        background: '#FFF0F5',
        accent: '#FF1493',
        accentSecondary: '#DB7093'
      },
      decorations: ['💕', '💝', '💖', '🌹', '💐', '🦋'],
      features: [
        'Herz-Animationen',
        'Rosa & Rote Akzente',
        'Romantische Icons',
        'Liebe liegt in der Luft'
      ]
    },
    {
      id: 'mothersday',
      name: 'Muttertag',
      description: 'Blumige Atmosphäre für Mama',
      icon: '💐',
      season: 'Frühling',
      period: 'Mai',
      colors: {
        primary: '#E6A8D7',
        secondary: '#C48FC3',
        background: '#FCF5F8',
        accent: '#D946A4',
        accentSecondary: '#F472B6'
      },
      decorations: ['💐', '🌸', '🌺', '💝', '🦋', '🌷'],
      features: [
        'Blumen-Animationen',
        'Zarte Pastelltöne',
        'Mama-Icons',
        'Liebevolle Akzente'
      ]
    }
  ];

  const activateTheme = async (theme) => {
    try {
      const token = localStorage.getItem('token');
      
      // Generate decorations
      const decorations = theme.id !== 'standard' ? generateDecorations(theme.id) : [];
      
      // Use custom colors if available, otherwise use default theme colors
      const colorsToUse = customColors[theme.id] 
        ? { ...theme.colors, ...customColors[theme.id] }
        : theme.colors;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/themes/seasonal/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          seasonalTheme: theme.id,
          colors: colorsToUse,
          decorations: decorations
        })
      });

      if (response.ok) {
        setActiveSeason(theme.id);
        await setActiveTheme(colorsToUse);
        toast.success(`${theme.name} wurde aktiviert!`);
        
        // Reload page to show new theme
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast.error('Fehler beim Aktivieren des Themes');
      }
    } catch (error) {
      console.error('Error activating theme:', error);
      toast.error('Fehler beim Aktivieren des Themes');
    }
  };

  const openPreview = (theme) => {
    // Use custom colors if available for preview
    const colorsToUse = customColors[theme.id] 
      ? { ...theme.colors, ...customColors[theme.id] }
      : theme.colors;
    setPreviewTheme({ ...theme, colors: colorsToUse });
    setPreviewOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Saisonale Themes</h1>
          <p className="text-gray-600">Passen Sie Ihre Homepage an die Jahreszeit an</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {seasonalThemes.map((theme) => (
            <Card 
              key={theme.id}
              className={`hover:shadow-xl transition-all cursor-pointer ${
                activeSeason === theme.id ? 'ring-4 ring-green-500' : ''
              }`}
            >
              <CardHeader 
                className="pb-4"
                style={{ 
                  background: `linear-gradient(135deg, ${theme.colors.background} 0%, ${theme.colors.primary} 100%)` 
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-4xl">{theme.icon}</div>
                    <div>
                      <CardTitle className="text-xl">{theme.name}</CardTitle>
                      {theme.period && (
                        <p className="text-sm text-gray-600">{theme.period}</p>
                      )}
                    </div>
                  </div>
                  {activeSeason === theme.id && (
                    <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                      <Check className="h-4 w-4" />
                      Aktiv
                    </div>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="pt-4">
                <p className="text-gray-700 mb-4">{theme.description}</p>
                
                {/* Color Preview with Color Pickers */}
                <div className="mb-4">
                  <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    Farben (klicken zum Ändern):
                  </p>
                  <div className="flex gap-2">
                    <ColorPicker
                      color={customColors[theme.id]?.primary || theme.colors.primary}
                      onChange={(color) => setCustomColors(prev => ({
                        ...prev,
                        [theme.id]: { ...(prev[theme.id] || theme.colors), primary: color }
                      }))}
                      label="Primärfarbe"
                    />
                    <ColorPicker
                      color={customColors[theme.id]?.accent || theme.colors.accent}
                      onChange={(color) => setCustomColors(prev => ({
                        ...prev,
                        [theme.id]: { ...(prev[theme.id] || theme.colors), accent: color }
                      }))}
                      label="Akzentfarbe"
                    />
                    {theme.colors.accentSecondary && (
                      <ColorPicker
                        color={customColors[theme.id]?.accentSecondary || theme.colors.accentSecondary}
                        onChange={(color) => setCustomColors(prev => ({
                          ...prev,
                          [theme.id]: { ...(prev[theme.id] || theme.colors), accentSecondary: color }
                        }))}
                        label="Akzent 2"
                      />
                    )}
                  </div>
                  {customColors[theme.id] && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="mt-2 text-xs"
                      onClick={() => setCustomColors(prev => {
                        const newColors = { ...prev };
                        delete newColors[theme.id];
                        return newColors;
                      })}
                    >
                      Auf Standard zurücksetzen
                    </Button>
                  )}
                </div>

                {/* Decorations Preview */}
                {theme.decorations && theme.decorations.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold mb-2">Dekorationen:</p>
                    <div className="flex flex-wrap gap-2">
                      {theme.decorations.slice(0, 6).map((emoji, i) => (
                        <span key={i} className="text-2xl">{emoji}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features */}
                {theme.features && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold mb-2">Features:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {theme.features.slice(0, 3).map((feature, i) => (
                        <li key={i}>• {feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => openPreview(theme)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Vorschau
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => activateTheme(theme)}
                    disabled={activeSeason === theme.id}
                    style={{
                      backgroundColor: activeSeason === theme.id ? '#9CA3AF' : theme.colors.accent,
                      color: 'white'
                    }}
                  >
                    {activeSeason === theme.id ? 'Aktiv' : 'Aktivieren'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info Card */}
        <Card className="mt-8 bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Sparkles className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-blue-900 mb-2">So funktionieren saisonale Themes:</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ Klicken Sie auf "Vorschau", um zu sehen, wie Ihre Homepage aussehen wird</li>
                  <li>✓ Klicken Sie auf "Aktivieren", um das Theme anzuwenden</li>
                  <li>✓ Die Dekorationen erscheinen automatisch auf allen Seiten</li>
                  <li>✓ Farben werden site-wide angepasst</li>
                  <li>✓ Sie können jederzeit zum Standard-Design zurückkehren</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <span className="text-3xl">{previewTheme?.icon}</span>
                {previewTheme?.name} - Live-Vorschau
              </DialogTitle>
              <Button variant="ghost" size="sm" onClick={() => setPreviewOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>
          
          {previewTheme && (
            <div 
              className="relative rounded-lg border-4 p-8 min-h-[500px]"
              style={{ 
                backgroundColor: previewTheme.colors.background,
                borderColor: previewTheme.colors.accent
              }}
            >
              {/* Decorations */}
              {previewTheme.decorations && previewTheme.decorations.length > 0 && (
                <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
                  {Array.from({ length: 15 }).map((_, i) => (
                    <div
                      key={i}
                      className="absolute animate-float"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        fontSize: `${20 + Math.random() * 20}px`,
                        opacity: 0.3 + Math.random() * 0.4,
                        animation: `float ${3 + Math.random() * 3}s ease-in-out infinite`,
                        animationDelay: `${Math.random() * 2}s`
                      }}
                    >
                      {previewTheme.decorations[Math.floor(Math.random() * previewTheme.decorations.length)]}
                    </div>
                  ))}
                </div>
              )}

              {/* Preview Content */}
              <div className="relative z-10">
                {/* Hero Section */}
                <div className="text-center mb-8">
                  <h1 
                    className="text-4xl font-bold mb-4"
                    style={{ color: previewTheme.colors.accent }}
                  >
                    Willkommen bei Homeyoursoul
                  </h1>
                  <p className="text-lg text-gray-700 mb-6">
                    Entdecken Sie unsere exklusive Kollektion
                  </p>
                  <button 
                    className="px-8 py-3 rounded-lg text-white font-semibold hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: previewTheme.colors.accent }}
                  >
                    Jetzt entdecken
                  </button>
                </div>

                {/* Product Cards Preview */}
                <div className="grid grid-cols-3 gap-4 mt-8">
                  {[1, 2, 3].map((i) => (
                    <div 
                      key={i}
                      className="bg-white rounded-lg p-4 shadow-lg"
                      style={{ borderTop: `4px solid ${previewTheme.colors.accent}` }}
                    >
                      <div 
                        className="h-32 rounded mb-3"
                        style={{ backgroundColor: previewTheme.colors.primary }}
                      />
                      <h3 className="font-semibold mb-2">Produkt {i}</h3>
                      <p className="text-sm text-gray-600 mb-2">Beschreibung</p>
                      <button
                        className="w-full py-2 rounded text-white text-sm font-semibold"
                        style={{ backgroundColor: previewTheme.colors.accent }}
                      >
                        In den Warenkorb
                      </button>
                    </div>
                  ))}
                </div>

                {/* Footer Preview */}
                <div 
                  className="mt-8 p-6 rounded-lg text-center"
                  style={{ 
                    backgroundColor: previewTheme.colors.accentSecondary || previewTheme.colors.accent,
                    color: 'white'
                  }}
                >
                  <p className="font-semibold">Footer mit Theme-Farben</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Schließen
            </Button>
            {previewTheme && (
              <Button 
                onClick={() => {
                  activateTheme(previewTheme);
                  setPreviewOpen(false);
                }}
                style={{ backgroundColor: previewTheme.colors.accent, color: 'white' }}
              >
                Theme aktivieren
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          25% {
            transform: translateY(-20px) rotate(5deg);
          }
          50% {
            transform: translateY(-10px) rotate(-5deg);
          }
          75% {
            transform: translateY(-25px) rotate(3deg);
          }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default SeasonalThemesNew;
